/**
 * Obstacle.js - Obstacle Entity for Memex Racing Game
 * 
 * Various obstacles that increase game difficulty by hindering player movement.
 * Includes static, dynamic, and environmental hazards.
 */

import Phaser from 'phaser';

/**
 * Obstacle class for creating various hazards on the track
 */
export class Obstacle extends Phaser.Physics.Arcade.Sprite {
  constructor(scene, x, y, obstacleType, config = {}) {
    // Create sprite with appropriate texture
    const texture = obstacleType.texture || 'obstacle_default';
    super(scene, x, y, texture);
    
    // Add to scene and enable physics
    scene.add.existing(this);
    scene.physics.add.existing(this);
    
    // Store obstacle properties
    this.obstacleType = obstacleType;
    this.scene = scene;
    this.active = true;
    
    // Physics setup
    this.body.setImmovable(true);
    
    // Set size based on obstacle type
    const size = obstacleType.size || { width: 64, height: 64 };
    this.setDisplaySize(size.width, size.height);
    this.body.setSize(size.width * 0.8, size.height * 0.8); // Slightly smaller hitbox
    
    // Visual setup - ABOVE players for visibility
    this.setDepth(350); // Above players (200-260) and most UI elements
    this.setTint(obstacleType.tint || 0xffffff);
    
    // Movement properties for dynamic obstacles
    this.movementType = obstacleType.movement || 'static';
    this.moveSpeed = obstacleType.moveSpeed || 0;
    this.moveDirection = obstacleType.moveDirection || 0;
    this.moveAmplitude = obstacleType.moveAmplitude || 0;
    this.originalX = x;
    this.originalY = y;
    this.moveTimer = 0;
    
    // Effect properties
    this.effectType = obstacleType.effect;
    this.effectDuration = obstacleType.effectDuration || 1000;
    this.effectStrength = obstacleType.effectStrength || 1;
    
    // Warning system
    this.hasWarning = obstacleType.hasWarning || false;
    this.warningTime = obstacleType.warningTime || 1000;
    this.isActivated = !this.hasWarning; // If has warning, starts deactivated
    
    // Lifetime
    this.lifetime = config.lifetime || 30000; // 30 seconds default
    this.spawnTime = scene.time.now;
    
    // Create warning effect if needed
    if (this.hasWarning) {
      this.createWarning();
    }
    
    // Setup animations/effects
    this.setupVisualEffects();
    
    console.log(`[Obstacle] Created ${obstacleType.name} at (${x}, ${y})`);
  }
  
  /**
   * Create warning effect before obstacle activates
   */
  createWarning() {
    // Create flashing effect
    this.setAlpha(0.3);
    
    // Warning animation
    this.scene.tweens.add({
      targets: this,
      alpha: { from: 0.3, to: 0.8 },
      duration: 200,
      yoyo: true,
      repeat: Math.floor(this.warningTime / 400),
      onComplete: () => {
        this.isActivated = true;
        this.setAlpha(1);
        this.createActivationEffect();
      }
    });
    
    // Create warning circle
    if (this.scene && this.scene.add) {
      const warningCircle = this.scene.add.circle(this.x, this.y, 40, 0xff0000, 0.3);
      warningCircle.setDepth(this.depth - 1);
      
      this.scene.tweens.add({
        targets: warningCircle,
        scale: { from: 1, to: 1.5 },
        alpha: { from: 0.3, to: 0 },
        duration: this.warningTime,
        onComplete: () => warningCircle.destroy()
      });
    }
  }
  
  /**
   * Create activation effect when obstacle becomes active
   */
  createActivationEffect() {
    // Flash effect
    this.scene.tweens.add({
      targets: this,
      scaleX: 1.2,
      scaleY: 1.2,
      duration: 100,
      yoyo: true
    });
    
    // Particle burst
    const particles = [];
    for (let i = 0; i < 8; i++) {
      const angle = (Math.PI * 2 * i) / 8;
      const particle = this.scene.add.circle(
        this.x + Math.cos(angle) * 10,
        this.y + Math.sin(angle) * 10,
        3,
        this.obstacleType.tint || 0xff0000
      );
      particle.setDepth(this.depth + 1);
      
      this.scene.tweens.add({
        targets: particle,
        x: particle.x + Math.cos(angle) * 30,
        y: particle.y + Math.sin(angle) * 30,
        alpha: 0,
        scale: 0,
        duration: 500,
        onComplete: () => particle.destroy()
      });
    }
  }
  
  /**
   * Setup visual effects based on obstacle type
   */
  setupVisualEffects() {
    // REMOVED: setStroke doesn't exist on Sprites, only on Graphics objects
    // We'll create outline using circle shapes instead
    
    // Add outline and glow effect for better visibility
    if (this.scene && this.scene.add) {
      const glowColor = this.obstacleType.tint || 0xff0000;
      
      // Create white outline ring for visibility
      const outlineRing = this.scene.add.circle(this.x, this.y, 
        Math.max(this.width, this.height) / 2 + 5, 
        0xffffff, 0.8);
      outlineRing.setDepth(this.depth - 1);
      this.outlineRing = outlineRing;
      
      // Create a glow ring around obstacle
      const glowRing = this.scene.add.circle(this.x, this.y, 
        Math.max(this.width, this.height) / 2 + 10, 
        glowColor, 0.3);
      glowRing.setDepth(this.depth - 2);
      
      // Pulsing glow effect
      this.scene.tweens.add({
        targets: glowRing,
        alpha: { from: 0.3, to: 0.6 },
        scale: { from: 1, to: 1.2 },
        duration: 1000,
        yoyo: true,
        repeat: -1
      });
      
      // Store reference for cleanup
      this.glowRing = glowRing;
    }
    
    switch (this.obstacleType.visualEffect) {
      case 'pulse':
        this.scene.tweens.add({
          targets: this,
          scale: { from: 1, to: 1.2 },
          duration: 800,
          yoyo: true,
          repeat: -1
        });
        break;
        
      case 'rotate':
        this.scene.tweens.add({
          targets: this,
          angle: 360,
          duration: 1500,
          repeat: -1
        });
        break;
        
      case 'glow':
        // Enhanced glow with particles
        if (this.scene && this.scene.add) {
          // Add floating particles around obstacle
          for (let i = 0; i < 4; i++) {
            const particle = this.scene.add.circle(
              this.x + (Math.random() - 0.5) * 50,
              this.y + (Math.random() - 0.5) * 50,
              3, this.obstacleType.tint || 0xff0000, 0.8
            );
            particle.setDepth(this.depth + 1);
            
            this.scene.tweens.add({
              targets: particle,
              x: particle.x + (Math.random() - 0.5) * 40,
              y: particle.y + (Math.random() - 0.5) * 40,
              alpha: { from: 0.8, to: 0.2 },
              duration: 2000 + Math.random() * 1000,
              repeat: -1,
              yoyo: true
            });
          }
        }
        break;
    }
  }
  
  /**
   * Update obstacle behavior
   */
  update(time, delta) {
    if (!this.active) return;
    
    // Check lifetime
    if (time - this.spawnTime > this.lifetime) {
      this.destroy();
      return;
    }
    
    // Update movement for dynamic obstacles
    if (this.isActivated && this.movementType !== 'static') {
      this.updateMovement(time, delta);
    }
    
    this.moveTimer += delta;
  }
  
  /**
   * Update movement patterns for dynamic obstacles
   */
  updateMovement(time, delta) {
    switch (this.movementType) {
      case 'oscillate':
        // Move back and forth
        const oscillation = Math.sin(this.moveTimer * 0.001 * this.moveSpeed) * this.moveAmplitude;
        if (this.moveDirection === 'horizontal') {
          this.x = this.originalX + oscillation;
        } else {
          this.y = this.originalY + oscillation;
        }
        break;
        
      case 'rotate':
        // Rotate around original position
        const angle = this.moveTimer * 0.001 * this.moveSpeed;
        this.x = this.originalX + Math.cos(angle) * this.moveAmplitude;
        this.y = this.originalY + Math.sin(angle) * this.moveAmplitude;
        this.rotation = angle;
        break;
        
      case 'patrol':
        // Move in a pattern
        const patrolSpeed = this.moveSpeed * delta * 0.001;
        this.x += Math.cos(this.moveDirection) * patrolSpeed;
        this.y += Math.sin(this.moveDirection) * patrolSpeed;
        
        // Reverse direction at boundaries
        const margin = 50;
        if (this.x < margin || this.x > this.scene.trackWidth - margin) {
          this.moveDirection = Math.PI - this.moveDirection;
        }
        if (this.y < margin || this.y > this.scene.trackHeight - margin) {
          this.moveDirection = -this.moveDirection;
        }
        break;
        
      case 'pulse':
        // Periodically activate/deactivate
        const pulseActive = Math.sin(this.moveTimer * 0.001 * this.moveSpeed) > 0;
        this.isActivated = pulseActive;
        this.setAlpha(pulseActive ? 1 : 0.3);
        break;
    }
  }
  
  /**
   * Apply effect to player on collision
   */
  applyEffect(player) {
    if (!this.isActivated || !player || player.hasImmunity) return;
    
    console.log(`[Obstacle] ${this.obstacleType.name} hit player ${player.index}`);
    
    switch (this.effectType) {
      case 'slow':
        // Reduce player speed
        player.speedMultiplier *= this.effectStrength;
        player.setTint(0x888888);
        
        this.scene.time.delayedCall(this.effectDuration, () => {
          player.speedMultiplier = 1;
          player.clearTint();
        });
        break;
        
      case 'stop':
        // Stop player completely
        player.paralyzed = true;
        player.setTint(0xff0000);
        
        this.scene.time.delayedCall(this.effectDuration, () => {
          player.paralyzed = false;
          player.clearTint();
        });
        break;
        
      case 'spin':
        // Make player spin and lose control
        const originalDirection = player.moveDirection;
        player.moveDirection = Math.random() * Math.PI * 2;
        
        this.scene.tweens.add({
          targets: player,
          angle: 360,
          duration: this.effectDuration,
          onComplete: () => {
            player.angle = 0;
            player.moveDirection = originalDirection + (Math.random() - 0.5) * Math.PI;
          }
        });
        break;
        
      case 'knockback':
        // Push player away
        const angle = Math.atan2(player.y - this.y, player.x - this.x);
        const force = this.effectStrength * 100;
        player.x += Math.cos(angle) * force;
        player.y += Math.sin(angle) * force;
        
        // Flash effect
        player.setTint(0xffff00);
        this.scene.time.delayedCall(200, () => player.clearTint());
        break;
        
      case 'teleport':
        // Teleport player backward
        const teleportDistance = this.effectStrength * 200;
        player.x -= teleportDistance;
        
        // Teleport effect
        const flash = this.scene.add.circle(player.x, player.y, 30, 0x00ffff, 0.8);
        this.scene.tweens.add({
          targets: flash,
          scale: 2,
          alpha: 0,
          duration: 500,
          onComplete: () => flash.destroy()
        });
        break;
        
      case 'slide':
        // Make player slide with reduced control
        player.momentum = { x: player.body.velocity.x, y: player.body.velocity.y };
        player.slideTimer = this.effectDuration;
        player.setTint(0x00ffff);
        
        this.scene.time.delayedCall(this.effectDuration, () => {
          player.slideTimer = 0;
          player.clearTint();
        });
        break;
        
      case 'magnetize':
        // Pull player toward obstacle
        const pullAngle = Math.atan2(this.y - player.y, this.x - player.x);
        const pullForce = this.effectStrength * 2;
        
        const pullInterval = this.scene.time.addEvent({
          delay: 50,
          repeat: this.effectDuration / 50,
          callback: () => {
            player.x += Math.cos(pullAngle) * pullForce;
            player.y += Math.sin(pullAngle) * pullForce;
          }
        });
        break;
    }
    
    // Create impact effect
    this.createImpactEffect(player);
  }
  
  /**
   * Create visual impact effect
   */
  createImpactEffect(player) {
    // Screen shake for major impacts
    if (this.effectType === 'stop' || this.effectType === 'knockback') {
      this.scene.cameras.main.shake(200, 0.01);
    }
    
    // Impact particles
    for (let i = 0; i < 5; i++) {
      const particle = this.scene.add.circle(
        player.x + (Math.random() - 0.5) * 20,
        player.y + (Math.random() - 0.5) * 20,
        2 + Math.random() * 3,
        0xffff00
      );
      particle.setDepth(200);
      
      this.scene.tweens.add({
        targets: particle,
        y: particle.y - 20 - Math.random() * 20,
        alpha: 0,
        scale: 0,
        duration: 500,
        onComplete: () => particle.destroy()
      });
    }
  }
  
  /**
   * Destroy obstacle
   */
  destroy() {
    // Clean up outline ring
    if (this.outlineRing) {
      this.outlineRing.destroy();
      this.outlineRing = null;
    }
    
    // Clean up glow ring
    if (this.glowRing) {
      this.glowRing.destroy();
      this.glowRing = null;
    }
    
    // Create destruction effect
    if (this.scene) {
      for (let i = 0; i < 6; i++) {
        const angle = (Math.PI * 2 * i) / 6;
        const particle = this.scene.add.rectangle(
          this.x + Math.cos(angle) * 10,
          this.y + Math.sin(angle) * 10,
          8, 8,
          this.obstacleType.tint || 0x888888
        );
        particle.setDepth(this.depth);
        
        this.scene.tweens.add({
          targets: particle,
          x: particle.x + Math.cos(angle) * 40,
          y: particle.y + Math.sin(angle) * 40,
          alpha: 0,
          angle: 360,
          scale: 0,
          duration: 600,
          onComplete: () => particle.destroy()
        });
      }
    }
    
    super.destroy();
  }
}

/**
 * Predefined obstacle types
 */
export const OBSTACLE_TYPES = {
  // Static Obstacles
  OIL_SLICK: {
    name: 'oil_slick',
    texture: 'obstacle_oil',
    tint: 0x222222,
    size: { width: 120, height: 120 },
    effect: 'spin',
    effectDuration: 1500,
    effectStrength: 1,
    movement: 'static',
    visualEffect: 'pulse',
    hasWarning: false,
    rarity: 'common',
    spawnWeight: 0.2
  },
  
  MUD_PATCH: {
    name: 'mud_patch',
    texture: 'obstacle_mud',
    tint: 0x654321,
    size: { width: 140, height: 140 },
    effect: 'slow',
    effectDuration: 2000,
    effectStrength: 0.5,
    movement: 'static',
    visualEffect: null,
    hasWarning: false,
    rarity: 'common',
    spawnWeight: 0.18
  },
  
  ROCK_BARRIER: {
    name: 'rock_barrier',
    texture: 'obstacle_rock',
    tint: 0x808080,
    size: { width: 100, height: 100 },
    effect: 'knockback',
    effectDuration: 0,
    effectStrength: 1.5,
    movement: 'static',
    visualEffect: null,
    hasWarning: false,
    rarity: 'common',
    spawnWeight: 0.15
  },
  
  SPIKE_STRIP: {
    name: 'spike_strip',
    texture: 'obstacle_spikes',
    tint: 0xc0c0c0,
    size: { width: 120, height: 40 },
    effect: 'stop',
    effectDuration: 1000,
    effectStrength: 1,
    movement: 'static',
    visualEffect: 'glow',
    hasWarning: true,
    warningTime: 1500,
    rarity: 'uncommon',
    spawnWeight: 0.1
  },
  
  // Dynamic Obstacles
  MOVING_BARRIER: {
    name: 'moving_barrier',
    texture: 'obstacle_barrier',
    tint: 0xff6600,
    size: { width: 100, height: 40 },
    effect: 'knockback',
    effectDuration: 0,
    effectStrength: 2,
    movement: 'oscillate',
    moveDirection: 'horizontal',
    moveSpeed: 2,
    moveAmplitude: 150,
    visualEffect: 'glow',
    hasWarning: false,
    rarity: 'uncommon',
    spawnWeight: 0.08
  },
  
  ROTATING_BLADE: {
    name: 'rotating_blade',
    texture: 'obstacle_blade',
    tint: 0xff0000,
    size: { width: 80, height: 80 },
    effect: 'knockback',
    effectDuration: 0,
    effectStrength: 3,
    movement: 'rotate',
    moveSpeed: 3,
    moveAmplitude: 0,
    visualEffect: 'rotate',
    hasWarning: true,
    warningTime: 2000,
    rarity: 'rare',
    spawnWeight: 0.05
  },
  
  LASER_BEAM: {
    name: 'laser_beam',
    texture: 'obstacle_laser',
    tint: 0x00ff00,
    size: { width: 200, height: 20 },
    effect: 'stop',
    effectDuration: 1500,
    effectStrength: 1,
    movement: 'pulse',
    moveSpeed: 1,
    visualEffect: 'glow',
    hasWarning: true,
    warningTime: 1000,
    rarity: 'rare',
    spawnWeight: 0.06
  },
  
  // Environmental Hazards
  TRAP_DOOR: {
    name: 'trap_door',
    texture: 'obstacle_trapdoor',
    tint: 0x8b4513,
    size: { width: 80, height: 80 },
    effect: 'teleport',
    effectDuration: 0,
    effectStrength: 1,
    movement: 'static',
    visualEffect: 'pulse',
    hasWarning: true,
    warningTime: 800,
    rarity: 'rare',
    spawnWeight: 0.04
  },
  
  ICE_PATCH: {
    name: 'ice_patch',
    texture: 'obstacle_ice',
    tint: 0x87ceeb,
    size: { width: 120, height: 120 },
    effect: 'slide',
    effectDuration: 2000,
    effectStrength: 1,
    movement: 'static',
    visualEffect: 'glow',
    hasWarning: false,
    rarity: 'uncommon',
    spawnWeight: 0.12
  },
  
  MAGNETIC_FIELD: {
    name: 'magnetic_field',
    texture: 'obstacle_magnet',
    tint: 0xff00ff,
    size: { width: 100, height: 100 },
    effect: 'magnetize',
    effectDuration: 1000,
    effectStrength: 3,
    movement: 'pulse',
    moveSpeed: 2,
    visualEffect: 'pulse',
    hasWarning: false,
    rarity: 'rare',
    spawnWeight: 0.05
  }
};

export default Obstacle;